import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import RefreshButton from "@/components/RefreshButton";
import RouteMap from "@/components/RouteMap";
import RoutePanel from "@/components/RoutePanel";
import MemberDetailDialog from "@/components/MemberDetailDialog";
import MemberFormDialog from "@/components/MemberFormDialog";
import QuickSearch from "@/components/QuickSearch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { normalize } from "@/lib/utils";

export default function RoutePage() {
  const { canWrite } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setMembers(await api.listMembers());
      } catch {
        toast.error("Üyeler yüklenemedi");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = normalize(search);
    if (!q) return members;
    const terms = q.split(" ").filter(Boolean);
    return members.filter((m) => {
      const hay = normalize(
        [m.name, m.company, m.address, m.status, m.phone, m.notes].filter(Boolean).join(" ")
      );
      return terms.every((t) => hay.includes(t));
    });
  }, [members, search]);

  const handleSaved = (member) => {
    setMembers((list) => list.map((m) => (m.id === member.id ? member : m)));
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] dark:bg-neutral-950">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-5 sm:py-6 space-y-4">
        {/* Başlık ve geri dönüş */}
        <div className="flex items-start gap-3">
          <Link to="/" className="shrink-0">
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10"
              aria-label="Panele dön"
              data-testid="rota-back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
              İnegöl Ticaret ve Sanayi Odası
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-0.5 text-neutral-950 dark:text-neutral-50">
              Saha Rotası
            </h1>
          </div>
        </div>

        {/* Arama */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Mahalle, sokak, isim veya şirket ara..."
            className="pl-10 h-11 bg-white dark:bg-neutral-900 dark:border-neutral-800"
            data-testid="rota-search"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded hover:bg-neutral-100 dark:hover:bg-neutral-800"
              aria-label="Aramayı temizle"
            >
              <X className="h-3.5 w-3.5 text-neutral-500" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-neutral-500 dark:text-neutral-400 text-sm">
            Yükleniyor...
          </div>
        ) : (
          <>
            <RouteMap members={filtered} onOpenDetail={(m) => setDetail(m)} />
            <RoutePanel members={filtered} onOpenDetail={(m) => setDetail(m)} />
          </>
        )}
      </main>

      <QuickSearch members={members} onOpenDetail={(m) => setDetail(m)} />

      <MemberDetailDialog
        member={detail}
        onOpenChange={(open) => !open && setDetail(null)}
        canWrite={canWrite}
        onEdit={(m) => {
          setDetail(null);
          setEditing(m);
          setFormOpen(true);
        }}
      />
      <MemberFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        member={editing}
        onSaved={handleSaved}
      />

      {/* Mobil yenile dugmesi */}
      <RefreshButton />
    </div>
  );
}
